import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { Map, List } from 'immutable'
import { compose } from 'recompose'
import { connect as connectRedux } from 'react-redux'
import _isNil from 'lodash/isNil'
import { getBoundActions } from '../../actions/index'
import {
  TYPE_CONTENT_UNKNOWN,
  TYPE_CONTENT_PERSON,
  TYPE_CONTENT_TAG,
  TYPE_CONTENT_PRODUCT,
} from '../../services/content-type/index'
import { EN } from '../../services/languages/constants'
import { getAuthIsLoggedIn } from '../../services/auth/index'
import { selectUserNodeInfoById } from '../../services/user-node-info/selectors'
import { selectUserLanguages } from '../../services/user/index'
import { getPrimary } from '../../services/languages/index'
import TileV2Hero from './Tile.v2Hero'
import TileV2Info from './Tile.v2Info'
import TileV2InfoMetadata from './Tile.v2InfoMetadata'
import TileV2Placeholder from './Tile.v2Placeholder'
import TileV2BottomMetadata from './Tile.v2BottomMetadata'
import {
  TILE_V2_DISPLAY_DEFAULT,
  TILE_V2_DISPLAY_VERTICAL,
  TILE_V2_DISPLAY_ROUND,
} from './constants'
import { getTileV2ClassName } from './utils'

/**
 * Tile v2 component
 * @param {Object} props - The props
 * @param {Map} props.data - The content data
 * @param {String} props.contentType - The type of content
 * @param {Boolean} props.disablePlaceholder - Whether or not to show the loading placeholder
 * @param {JSX.Element|null} props.editComponent - The edit component from playlists
 * @param {String} props.display - The display type
 * @param {Bookean} props.touchable - Whether or not the device is touch capable
 * @param {Bookean} props.hideLabels - Whether or not labels are hidden
 * @param {Bookean} props.hideTags - Whether or not tags are hidden
 *  or watch history
 * @returns {JSX.Element} The tile component
 */
function TileV2 (props) {
  const {
    data,
    contentType,
    disablePlaceholder,
    editComponent,
    display,
    touchable,
  } = props
  const id = data.get('id')
  const noData = !data || !Map.isMap(data) || !data.size || !id || id <= 0
  const [isTouchable, setIsTouchable] = useState(null)
  const [isHovered, setIsHovered] = useState(false)
  const [mouseIn, setMouseIn] = useState(false)
  // const [hoverTimeout, setHoverTimeout] = useState(() => null)
  const hoverDelay = 450
  const handleMouseEnter = () => {
    setMouseIn(true)
  }
  const handleMouseLeave = () => {
    setMouseIn(false)
  }

  // Using local state to fix an invariant violation on the share page
  // all other pages are fine without this fix
  useEffect(() => {
    setIsTouchable(touchable)
  }, [touchable])

  // Setting and clearing the hover timeout
  useEffect(() => {
    let hoverTimeout

    if (mouseIn) {
      hoverTimeout = setTimeout(() => {
        setIsHovered(true)
      }, hoverDelay)
    } else {
      setIsHovered(false)
    }

    return () => {
      clearTimeout(hoverTimeout)
    }
  }, [mouseIn])

  if (disablePlaceholder && noData) {
    return null
  }

  if (noData || _isNil(isTouchable)) {
    return <TileV2Placeholder display={display} />
  }

  return (
    <div className={getTileV2ClassName(props, isHovered)}>
      {editComponent || null}
      <div
        className="tile-v2__wrapper"
        onMouseEnter={!isTouchable ? handleMouseEnter : null}
        onMouseLeave={!isTouchable ? handleMouseLeave : null}
      >
        <TileV2Hero
          {...props}
          hovered={editComponent ? false : isHovered}
          isTouchable={isTouchable}
        />
        <TileV2Info
          {...props}
          hovered={editComponent || contentType === TYPE_CONTENT_PRODUCT ? false : isHovered}
          isTouchable={isTouchable}
        />
        {
          contentType === TYPE_CONTENT_PERSON
            ? <TileV2InfoMetadata {...props} hovered={isHovered} />
            : null
        }
      </div>
      {
        contentType === TYPE_CONTENT_TAG
          || contentType === TYPE_CONTENT_PRODUCT
          ? <TileV2InfoMetadata
              {...props}
              hovered={contentType === TYPE_CONTENT_PRODUCT ? false : isHovered}
            />
          : <TileV2BottomMetadata {...props} />
      }
    </div>
  )
}

TileV2.propTypes = {
  data: ImmutablePropTypes.map.isRequired,
  staticText: ImmutablePropTypes.map.isRequired,
  auth: ImmutablePropTypes.map.isRequired,
  upstreamContext: ImmutablePropTypes.map,
  userNodeInfo: ImmutablePropTypes.map.isRequired,
  resolver: ImmutablePropTypes.map.isRequired,
  subscriptions: ImmutablePropTypes.list.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  contentType: PropTypes.string.isRequired,
  contentSubtype: PropTypes.string,
  touchable: PropTypes.bool.isRequired,
  sectionIndex: PropTypes.number,
  rowPlacement: PropTypes.number,
  adminTitle: PropTypes.string,
  index: PropTypes.number,
  display: PropTypes.oneOf([
    TILE_V2_DISPLAY_DEFAULT,
    TILE_V2_DISPLAY_VERTICAL,
    TILE_V2_DISPLAY_ROUND,
  ]),
  editComponent: PropTypes.element,
  legacyData: PropTypes.bool,
  disablePlaceholder: PropTypes.bool,
  pageLanguage: PropTypes.string,
  onClick: PropTypes.func,
  setEventDefault: PropTypes.func.isRequired,
  setUpstreamContext: PropTypes.func.isRequired,
  imageOptimization: PropTypes.bool,
  hideLabels: PropTypes.bool,
  hideTags: PropTypes.bool,
  displayMode: PropTypes.string,
}

TileV2.defaultProps = {
  data: Map(),
  upstreamContext: Map(),
  userNodeInfo: Map(),
  subscriptions: List(),
  display: TILE_V2_DISPLAY_DEFAULT,
  legacyData: false,
  disablePlaceholder: false,
  editComponent: null,
  imageOptimization: true,
  hideLabels: false,
  hideTags: false,
}

export default compose(
  connectRedux(
    (state, props) => {
      const { data } = props
      const isLoggedIn = getAuthIsLoggedIn(state.auth)
      const contentType = data.get('contentType', TYPE_CONTENT_UNKNOWN)
      const contentSubtype = data.get('contentSubtype')
      const id = data.get('id')
      const userNodeInfo = selectUserNodeInfoById(state, { id }) || Map()
      const userLanguages = selectUserLanguages(state)
      const editComponent = data.get('playlistEditComponent', null) || data.get('hideWatchedEditComponent', null)
      const userLanguage = getPrimary(state.user.getIn(['data', 'language']))

      return {
        isLoggedIn,
        contentType,
        contentSubtype,
        staticText: state.staticText.getIn(['data', 'tileV2', 'data']),
        touchable: state.app.getIn(['viewport', 'touchable'], false),
        auth: state.auth,
        userNodeInfo,
        subscriptions: state.auth.get('subscriptions', List()),
        country: state.auth.get('country'),
        userLanguage,
        userLanguages,
        resolver: state.resolver,
        pageLanguage: state.page.get('lang', EN),
        viewportWidth: state.app.getIn(['viewport', 'width']),
        editComponent,
      }
    },
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        setEventDefault: actions.eventTracking.setEventDefault,
        setUpstreamContext: actions.upstreamContext.setUpstreamContext,
      }
    },
  ),
)(TileV2)
